import { useState, useCallback, useRef, useEffect } from "react";
import { TipoVariacao, NivelVariacao, VariacaoV2 } from "../../../../types/Produto";

interface UseVariacoesProps {
  listasDePreco: any[];
  skuPai: string;
  idProduto: string | number;
  temEstoque: boolean;
}

interface UseVariacoesReturn {
  niveisVariacao: NivelVariacao[];
  variacoes: VariacaoV2[];
  setNivelTipo: (ordem: number, tipo: TipoVariacao) => void;
  setNivelValores: (ordem: number, valores: string[]) => void;
  addNivel: () => boolean;
  removeNivel: (ordem: number) => void;
  swapNiveis: () => void;
  regenerarGrade: () => void;
  updateVariacao: (index: number, field: string, value: any) => void;
  deleteVariacao: (index: number) => void;
  restaurarVariacoes: () => void;
  setVariacoes: React.Dispatch<React.SetStateAction<VariacaoV2[]>>;
  setNiveisVariacao: React.Dispatch<React.SetStateAction<NivelVariacao[]>>;
  replicarCampo: (field: string, value: any, precoIndex?: number) => void;
}

function gerarGrade(
  niveis: NivelVariacao[],
  listasDePreco: any[],
  skuPai: string,
  idProduto: string | number,
  variacoesExistentes: VariacaoV2[]
): VariacaoV2[] {
  if (niveis.length === 0) return [];

  const nivel0 = niveis[0];
  const nivel1 = niveis.length > 1 ? niveis[1] : null;

  const combinacoes: { principal: string; secundario?: string }[] = [];

  if (!nivel1) {
    nivel0.valores.forEach((val) => {
      combinacoes.push({ principal: val });
    });
  } else {
    nivel0.valores.forEach((val0) => {
      nivel1.valores.forEach((val1) => {
        combinacoes.push({ principal: val0, secundario: val1 });
      });
    });
  }

  const normalize = (s?: string) => (s || "").toLowerCase().trim();

  // Primeiro pass: resolver existentes vs novas
  const resultado = combinacoes.map((combo) => {
    const display = combo.secundario
      ? `${combo.principal} | ${combo.secundario}`
      : combo.principal;

    let existente = variacoesExistentes.find(
      (v) =>
        normalize(v.valor_principal) === normalize(combo.principal) &&
        normalize(v.valor_secundario) === normalize(combo.secundario)
    ) || (combo.secundario ? variacoesExistentes.find(
      (v) =>
        normalize(v.valor_principal) === normalize(combo.secundario) &&
        normalize(v.valor_secundario) === normalize(combo.principal)
    ) : null);

    if (!existente && !combo.secundario) {
      existente = variacoesExistentes.find(
        (v) => normalize(v.valor_principal) === normalize(combo.principal)
      );
    }

    return { combo, display, existente };
  });

  // Segundo pass: numerar todas sequencialmente
  return resultado.map((item, index) => {
    if (item.existente) {
      return { ...item.existente, variacao: item.display, valor_principal: item.combo.principal, valor_secundario: item.combo.secundario, sku: `${skuPai}-${index + 1}` };
    }

    const sku = skuPai ? `${skuPai}-${index + 1}` : `-${index + 1}`;

    return {
      id: "0",
      id_produto: idProduto,
      variacao: item.display,
      valor_principal: item.combo.principal,
      valor_secundario: item.combo.secundario,
      sku,
      estoque: "0",
      peso: null,
      ativo: "1" as const,
      precos: listasDePreco.map((tabela) => ({
        id: "0",
        id_variacao: "0",
        id_tabela: tabela.id,
        preco: "0",
      })),
    };
  });
}

export function useVariacoes({
  listasDePreco,
  skuPai,
  idProduto,
  temEstoque,
}: UseVariacoesProps): UseVariacoesReturn {
  const [niveisVariacao, setNiveisVariacao] = useState<NivelVariacao[]>([]);
  const [variacoes, setVariacoes] = useState<VariacaoV2[]>([]);

  // Refs para evitar recriação de callbacks quando props mudam
  const listasDePrecoRef = useRef(listasDePreco);
  const skuPaiRef = useRef(skuPai);
  const idProdutoRef = useRef(idProduto);
  const niveisRef = useRef(niveisVariacao);

  useEffect(() => { listasDePrecoRef.current = listasDePreco; }, [listasDePreco]);
  useEffect(() => { skuPaiRef.current = skuPai; }, [skuPai]);
  useEffect(() => { idProdutoRef.current = idProduto; }, [idProduto]);
  useEffect(() => { niveisRef.current = niveisVariacao; }, [niveisVariacao]);

  const regenerarGradeInternal = useCallback(
    (niveis: NivelVariacao[], existentes: VariacaoV2[]) => {
      const novas = gerarGrade(niveis, listasDePrecoRef.current, skuPaiRef.current, idProdutoRef.current, existentes);
      setVariacoes(novas);
      return novas;
    },
    []
  );

  const setNivelTipo = useCallback(
    (ordem: number, tipo: TipoVariacao) => {
      setNiveisVariacao((prev) => {
        const updated = [...prev];
        const existingIndex = updated.findIndex((n) => n.ordem === ordem);
        const valoresExistentes = existingIndex >= 0 ? updated[existingIndex].valores : [];
        const novoNivel: NivelVariacao = { tipo, valores: valoresExistentes, ordem };
        if (existingIndex >= 0) {
          updated[existingIndex] = novoNivel;
        } else {
          updated.push(novoNivel);
        }
        updated.sort((a, b) => a.ordem - b.ordem);
        // Regenerar grade se já tinha valores (troca de tipo)
        if (valoresExistentes.length > 0) {
          setVariacoes((prevVars) =>
            gerarGrade(updated, listasDePrecoRef.current, skuPaiRef.current, idProdutoRef.current, prevVars)
          );
        }
        return updated;
      });
    },
    []
  );

  const setNivelValores = useCallback(
    (ordem: number, valores: string[]) => {
      setNiveisVariacao((prev) => {
        const nivel = prev.find(n => n.ordem === ordem);
        const valoresAntigos = nivel?.valores || [];

        const updated = prev.map((n) =>
          n.ordem === ordem ? { ...n, valores } : n
        );

        // Detectar reorder (mesmo conjunto, ordem diferente): reordena as
        // variações existentes para seguir a nova ordem escolhida, sem
        // regenerar a grade. Assim preserva estoque/peso/preços e não cria
        // combos faltantes em produtos esparsos (ex: importados por planilha).
        if (valoresAntigos.length === valores.length && valoresAntigos.length > 0) {
          const setAntigos = new Set(valoresAntigos);
          const setNovos = new Set(valores);
          const mesmosValores =
            setAntigos.size === setNovos.size &&
            valoresAntigos.every((v) => setNovos.has(v));
          const ordemDiferente = valoresAntigos.some((v, i) => v !== valores[i]);
          if (mesmosValores && ordemDiferente) {
            const norm = (s?: string) => (s || "").toLowerCase().trim();
            const nivel0Valores = updated.find((n) => n.ordem === 0)?.valores || [];
            const nivel1Valores = updated.find((n) => n.ordem === 1)?.valores || [];
            const idx0 = new Map(nivel0Valores.map((v, i) => [norm(v), i] as const));
            const idx1 = new Map(nivel1Valores.map((v, i) => [norm(v), i] as const));
            const NAO_ENCONTRADO = Number.MAX_SAFE_INTEGER;
            const rankNivel = (idx: Map<string, number>, valor?: string) =>
              idx.has(norm(valor)) ? (idx.get(norm(valor)) as number) : NAO_ENCONTRADO;
            // sort é estável: combos não encontrados (esparsos) ficam no fim
            // mantendo a posição relativa anterior.
            setVariacoes((prevVars) =>
              [...prevVars].sort((a, b) => {
                const r0 = rankNivel(idx0, a.valor_principal) - rankNivel(idx0, b.valor_principal);
                if (r0 !== 0) return r0;
                return rankNivel(idx1, a.valor_secundario) - rankNivel(idx1, b.valor_secundario);
              })
            );
            return updated;
          }
        }

        // Detectar edição: mesmo tamanho, apenas um valor mudou
        if (valoresAntigos.length === valores.length && valoresAntigos.length > 0) {
          let editIndex = -1;
          let edits = 0;
          for (let i = 0; i < valores.length; i++) {
            if (valoresAntigos[i] !== valores[i]) {
              editIndex = i;
              edits++;
            }
          }
          if (edits === 1 && editIndex !== -1) {
            const valorAntigo = valoresAntigos[editIndex];
            const valorNovo = valores[editIndex];
            const campo = ordem === 0 ? 'valor_principal' : 'valor_secundario';
            const campoDisplay = ordem === 0 ? 'variacao' : 'variacao';

            setVariacoes(prevVars =>
              prevVars.map(v => {
                if (v[campo] === valorAntigo) {
                  const updatedV = { ...v, [campo]: valorNovo };
                  // Atualizar o display
                  updatedV.variacao = updatedV.valor_secundario
                    ? `${updatedV.valor_principal} | ${updatedV.valor_secundario}`
                    : updatedV.valor_principal;
                  return updatedV;
                }
                return v;
              })
            );
            return updated;
          }
        }

        // Caso contrário: adição/remoção, regenerar grade
        if (valoresAntigos.length > 0 || valores.length > 0) {
          setVariacoes((prevVars) =>
            gerarGrade(updated, listasDePrecoRef.current, skuPaiRef.current, idProdutoRef.current, prevVars)
          );
        }
        return updated;
      });
    },
    []
  );

  const addNivel = useCallback((): boolean => {
    if (niveisRef.current.length >= 2) return false;
    return true;
  }, []);

  const removeNivel = useCallback(
    (ordem: number) => {
      setNiveisVariacao((prev) => {
        const updated = prev.filter((n) => n.ordem !== ordem);
        const reindexed = updated.map((n, i) => ({ ...n, ordem: i }));
        setVariacoes((prevVars) =>
          gerarGrade(reindexed, listasDePrecoRef.current, skuPaiRef.current, idProdutoRef.current, prevVars)
        );
        return reindexed;
      });
    },
    []
  );

  const swapNiveis = useCallback(() => {
    setNiveisVariacao((prev) => {
      if (prev.length < 2) return prev;
      const swapped = [
        { ...prev[1], ordem: 0 },
        { ...prev[0], ordem: 1 },
      ];
      // Troca principal/secundario in-place. Não chama gerarGrade pra preservar
      // dados (estoque/peso/preços) e não criar combos faltantes em produtos esparsos.
      setVariacoes((prevVars) =>
        prevVars.map((v) => {
          const novoPrincipal = v.valor_secundario ?? "";
          const novoSecundario = v.valor_principal;
          return {
            ...v,
            valor_principal: novoPrincipal,
            valor_secundario: novoSecundario || undefined,
            variacao: novoSecundario
              ? `${novoPrincipal} | ${novoSecundario}`
              : novoPrincipal,
          };
        })
      );
      return swapped;
    });
  }, []);

  const regenerarGrade = useCallback(() => {
    regenerarGradeInternal(niveisRef.current, []);
  }, [regenerarGradeInternal]);

  const updateVariacao = useCallback(
    (index: number, field: string, value: any) => {
      setVariacoes((prev) => {
        const updated = [...prev];
        if (field.startsWith("preco_")) {
          const precoIndex = parseInt(field.split("_")[1]);
          updated[index] = {
            ...updated[index],
            precos: updated[index].precos.map((p, i) =>
              i === precoIndex ? { ...p, preco: value } : p
            ),
          };
        } else {
          updated[index] = { ...updated[index], [field]: value };
        }
        return updated;
      });
    },
    []
  );

  const deleteVariacao = useCallback((index: number) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ativo: "0" };
      return updated;
    });
  }, []);

  const restaurarVariacoes = useCallback(() => {
    const skuPai = skuPaiRef.current;
    setVariacoes(prev => prev.map((v, index) => ({
      ...v,
      sku: skuPai ? `${skuPai}-${index + 1}` : `-${index + 1}`,
      estoque: null,
      peso: null,
      ativo: "1" as const,
      precos: v.precos.map(p => ({ ...p, preco: "0" })),
    })));
  }, []);

  const replicarCampo = useCallback(
    (field: string, value: any, precoIndex?: number) => {
      setVariacoes((prev) =>
        prev.map((v) => {
          if (Number(v.ativo) !== 1) return v;
          if (field === "preco" && precoIndex !== undefined) {
            return {
              ...v,
              precos: v.precos.map((p, i) =>
                i === precoIndex ? { ...p, preco: value } : p
              ),
            };
          }
          return { ...v, [field]: value };
        })
      );
    },
    []
  );

  return {
    niveisVariacao,
    variacoes,
    setNivelTipo,
    setNivelValores,
    addNivel,
    removeNivel,
    swapNiveis,
    regenerarGrade,
    updateVariacao,
    deleteVariacao,
    restaurarVariacoes,
    setVariacoes,
    setNiveisVariacao,
    replicarCampo,
  };
}
